import { findOne } from 'rcd-cpp-workspace/utils/array';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Metadata, Schema, SchemaField, Workspace } from '../../types/Metadata';
import { Functions } from '../../types/types';
import { View } from '../../types/View';
import { setLoading } from '../Loader/loaderAction';
import {
  ViewState,
  updateViewState,
  updateSelectedView,
  updateSwimlaneData,
  toggleSwimlane,
  updateViewStateReqTime,
  updateViewGroup,
  toggleViewCheckbox,
  setUnsavedChanges,
  setTempFilters,
  updateInvalidCells
} from './viewReducer';
import { ViewType } from '../../enums/ViewType';
import {
  PlanningState,
  updatePlanningState
} from '../ContentPlanning/contentPlanningReducer';
import {
  clone,
  excludeEmptyFilters,
  formatDate,
  generateHashCode,
  isEnterpriseSchema,
  cleanFilter,
  parseField,
  getCardFields,
  showBackupMode
} from '../../utils';
import { UserSettingProps, UserSettings } from '../../types/UserSetting';
import { setUserSetting } from '../ContentPlanning/contentPlanningActions';
import { NodeType } from '../../enums/NodeType';
import { FieldType } from '../../enums/FieldType';
import { Op } from '../../enums/Op';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { FullStoryManager } from '../App/fullStory';
import { FSEvent } from '../../enums/FSEvent';
import { ExpiryRequestCallbackParams } from '../ItemKeepActiveModal/itemKeepActiveModal';
import { ViewCategory } from '../../enums/viewCategory';

export const enableEditMode =
  (): ElementAction<Functions, S> => async (dispatch, getState) => {
    const editModeEvent = () => async dispatch => {
      dispatch(
        FullStoryManager.getInstance().captureEvent(FSEvent.EditModeEntered)
      );
    };
    dispatch(editModeEvent());
    const viewState = getState().view;
    dispatch(updateViewState(viewState?.selected, { editMode: true }));
  };

export const disableEditMode =
  (): ElementAction<Functions, S> => async (dispatch, getState) => {
    const viewState = getState().view;
    dispatch(updateViewState(viewState?.selected, { editMode: false }));
  };

export const NUM_PER_PAGE = 25;
export type S = ContentPlanRootState;

const loadGroupData =
  (group: View.Group, limit?: number): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (!group || !view?.selected) return;
    const selView = view.selected;
    const { searchGroups } = getAPI();
    const { payload } = getSearchPayload(
      view,
      selView,
      planning,
      group,
      limit || NUM_PER_PAGE
    );
    await searchGroups(payload, ({ groups, data }) => {
      const g = findOne(groups, { id: group.id });
      dispatch(updateViewGroup(selView, g, data));
    });
  };

const loadSwimlane =
  (
    group: View.Group,
    swimlane: View.Group,
    limit?: number
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (!swimlane || !view?.selected) return;
    const selView = view.selected;
    const { searchGroups } = getAPI();
    const { payload } = getSearchPayload(
      view,
      selView,
      planning,
      group,
      limit || NUM_PER_PAGE,
      swimlane
    );
    await searchGroups(payload, r => {
      if (r) {
        const { data, groups } = r;
        const s = {
          ...swimlane,
          subgroups: groups?.map((g: any) => ({
            groupId: g.id,
            count: g.count,
            cardRefIds: g.cardRefIds
          }))
        };
        dispatch(
          updateSwimlaneData({
            groupId: group?.id,
            swimlaneId: swimlane.id,
            cards: data,
            swimlanes: [s]
          })
        );
      }
    });
  };

const getSchemaFields = (schema: Schema) => {
  const displayField = findOne(schema.props.fields, {
    field: schema.props.displayField
  })?.key;
  const fields = schema.props.fields
    .filter(f => f.fieldType != FieldType.custom && f.key != displayField)
    .map(f => f.key);

  // CN-5642: For the Planning Question view, insert Mission-Critical Priority
  // (RolePriority.name) as a column before Journey Step.
  if (schema.props.type === NodeType.DemandQuestion) {
    const pjsIndex = fields.indexOf('DemandQuestion.priorityJourneyStep');
    if (pjsIndex !== -1) {
      fields.splice(pjsIndex, 0, 'RolePriority.name');
    }
  }

  return [displayField, ...fields].map(field => ({ field }));
};

const getCategory = (schema: Schema): string => {
  if (schema?.props?.isManaged) return ViewCategory.ManagedData;
  if (isEnterpriseSchema(schema)) return ViewCategory.EnterpriseData;
  return ViewCategory.CustomData;
};

export const viewDataInSchema =
  (schema: Schema): ElementAction<Functions, S> =>
  async dispatch => {
    const fields = getSchemaFields(schema);
    const view = {
      id: schema.id,
      props: {
        refId: schema.props.refId,
        name: schema.props.name,
        description: schema.props.description,
        type: ViewType.Tabular,
        sortBy: [fields[0]],
        fields: fields.map(f => f.field || f),
        filters: fields?.map(
          f => ({ ...f, uuid: Math.random().toString(10) }) as any
        )
      },
      category: getCategory(schema),
      schema,
      dataSchema: schema,
      card: {
        props: {
          data: schema.props.type,
          fields
        }
      }
    } as View.Setting;
    dispatch(selectView(view));
  };

const applyUserSettingFilters = (
  view: View.Setting,
  userSettings: UserSettings
) => {
  const userSetting = userSettings && userSettings[view.props?.refId];
  // TODO - remove userSetting?.props?.settings?.filters
  const filters =
    userSetting?.props?.filters || userSetting?.props?.settings?.filters;
  const viewFilters = view.props?.filters || [];

  const emptyViewFilters = viewFilters
    .filter(f => !findOne(filters, { field: f.field }))
    .map(f => {
      const { conditions, ...props } = f;
      return props;
    });

  if (!filters?.length)
    return viewFilters?.map(f => ({ ...f, uuid: Math.random().toString(10) }));
  return filters
    .concat(emptyViewFilters)
    .map(f => ({ ...f, uuid: Math.random().toString(10) }));
};

const applyUserSettingSortBys = (
  view: View.Setting,
  userSettings: UserSettings
) => {
  const userSetting = userSettings && userSettings[view.props?.refId];
  const sortBys = userSetting?.props?.sortBys;
  const viewSortBys = view.props?.sortBy;
  if (!sortBys?.length) return viewSortBys;
  return sortBys;
};

export const selectView =
  (
    selected: View.Setting,
    selectedWorkspace?: Workspace
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const currentViewState = getState().view;
    const isViewingAs = !!currentViewState?.viewingAs;
    if (isViewingAs) {
      const { getUserSettings } = getAPI();
      const userSettings = await getUserSettings();
      dispatch(updatePlanningState({ userSettings }));
    }
    if (selectedWorkspace) {
      dispatch(updatePlanningState({ selectedWorkspace }));
    }
    dispatch(updateSelectedView(selected));
    try {
      if (selected) {
        dispatch(setLoading(true));
        const view: View.Setting = JSON.parse(JSON.stringify(selected));
        const { planning } = getState();
        const userSettings = planning.userSettings;

        const viewState = {
          data: null,
          groupData: {},
          filters: applyUserSettingFilters(view, userSettings),
          sortBy: applyUserSettingSortBys(view, userSettings),
          groups: []
        } as Partial<ViewState>;
        const { searchGroups } = getAPI();
        if (view.props?.type == ViewType.Kanban) {
          const { searchHash, payload } = getSearchPayload(
            viewState,
            view,
            planning
          );
          await searchGroups(
            payload,
            async ({ groups, info, swimlanes, data }, loadAdditional) => {
              if (loadAdditional) {
                dispatch(updateViewGroup(view, groups, data));
              } else {
                viewState.groups = groups;
                viewState.newCards = data;
                viewState.info = info;
                viewState.swimlanes = swimlanes;
                viewState.searchHash = searchHash;
                dispatch(updateViewState(selected, viewState));
                dispatch(setLoading(false));
              }
            }
          );
        } else {
          dispatch(updateViewState(selected, viewState));
          await dispatch(loadData({ forceReload: true }));
        }
        dispatch(FullStoryManager.getInstance().setPageProperties(view));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

const setSortBy =
  (sortBy: View.SortByField[]) => async (dispatch, getState) => {
    const selView = getState().view?.selected;
    dispatch(updateViewState(selView, { sortBy }));
    if (deepEquals(sortBy, selView?.props?.sortBy)) {
      dispatch(setViewSettings({ sortBys: null }, selView));
    } else {
      dispatch(setViewSettings({ sortBys: sortBy }, selView));
    }
    dispatch(reloadData());
  };

const setFilter =
  (
    filter: View.FiltersField,
    remove?: boolean,
    add?: boolean,
    addAnother?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    const { view } = getState();
    let updated = false;
    let index = null;
    if (!filter?.uuid && !remove && add) {
      filter.uuid = Math.random().toString(10);
    }
    const filters =
      view?.filters
        ?.map((f, i) => {
          if (
            (!filter.uuid && f.field == filter.field) ||
            (!!filter.uuid && f.uuid == filter.uuid)
          ) {
            index = i;
            if (remove) return null;
            updated = true;
            return { ...f, ...filter };
          }
          return f;
        })
        .filter(x => x) || [];
    if (addAnother) {
      filters.unshift(filter);
    } else if (!updated && !remove) {
      filters.unshift(filter);
    } else if (add && index !== null) {
      const f = filters.splice(index, 1)[0];
      if (f) filters.unshift(f);
    }
    const filtersWithValues = excludeEmptyFilters(filters);
    const filtersToSaveToDB = cleanFilter(filtersWithValues);
    dispatch(
      setViewSettings(
        { settings: {}, filters: filtersToSaveToDB },
        view?.selected
      )
    );
    dispatch(updateViewState(view?.selected, { filters }));
    if (!deepEquals(excludeEmptyFilters(view.filters), filtersWithValues)) {
      dispatch(reloadData());
    }
  };

const setFilters =
  (
    filtersIn: View.FiltersField[],
    remove?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view } = getState();
    let filters = filtersIn || view?.filters || [];
    filters = filters.filter(x => x);

    if (remove) {
      filters = filters.map(f => {
        return { ...f, conditions: undefined };
      });
    }

    const filtersWithValues = excludeEmptyFilters(filters);
    const filtersToSaveToDB = cleanFilter(filtersWithValues);

    dispatch(
      setViewSettings(
        { settings: {}, filters: filtersToSaveToDB },
        view?.selected
      )
    );
    dispatch(updateViewState(view?.selected, { filters }));
    if (!deepEquals(excludeEmptyFilters(view.filters), filtersWithValues)) {
      dispatch(reloadData());
    }
  };

const getBoardCardCount =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (!view?.selected) return;
    const selView = view.selected;
    if (selView?.props.type == ViewType.Kanban) {
      const { getBoardCardCount } = getAPI();
      const { payload } = getSearchPayload(view, selView, planning);
      return await getBoardCardCount(payload);
    }
  };

let timeout = null;
export const reloadData =
  (
    keepExpanded?: boolean,
    forceReload?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(async () => {
      const { view, planning } = getState();
      if (!view?.selected) return;
      const selView = view.selected;
      if (selView?.props.type == ViewType.Kanban) {
        const { searchGroups } = getAPI();
        const { searchHash, payload } = getSearchPayload(
          view,
          selView,
          planning
        );
        const reqTimeInMs = new Date().getTime();
        dispatch(updateViewStateReqTime(reqTimeInMs));
        await searchGroups(payload, (resp, loadAdditional) => {
          const { groups, info, swimlanes, data } = resp;
          if (loadAdditional) {
            dispatch(updateViewGroup(selView, groups, data));
            return;
          }
          if (swimlanes && keepExpanded) {
            const expandedSwimlanes = getState().view.swimlanes?.filter(
              s => s.expanded === true
            );
            if (expandedSwimlanes?.length) {
              swimlanes.forEach(s => {
                const expanded = findOne(expandedSwimlanes, { id: s.id });
                if (expanded) {
                  s.expanded = true;
                  s.subgroups = null;
                  s.prevSubGroups = expanded.subgroups;
                }
              });
            }
          }
          dispatch(
            updateViewState(selView, {
              reqTimeInMs,
              groups,
              searchHash,
              info,
              newCards: data,
              swimlanes
            })
          );
        });
      } else {
        dispatch(loadData({ forceReload }));
      }
    }, 600);
  };

const increaseNumOfResultsAndLoad =
  (cb?: Function): ElementAction<Functions, S> =>
  async (dispatch, getState, o) => {
    const { pagination } = getState().view;
    await loadData({
      forceReload: true,
      ignoreLoading: true,
      numOfResults: pagination.npp + NUM_PER_PAGE
    })(dispatch, getState, o);
    if (cb) cb();
  };

const getSearchFilters = (
  preApplyFilters: View.FiltersField[],
  filters: View.FiltersField[]
) => excludeEmptyFilters([].concat(preApplyFilters).concat(filters));

interface LoadDataOpts {
  forceReload?: boolean;
  ignoreLoading?: boolean;
  numOfResults?: number;
}
export const loadData =
  ({
    forceReload,
    ignoreLoading,
    numOfResults = NUM_PER_PAGE
  }: LoadDataOpts = {}): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { view, planning } = getState();
    if (!view?.selected) return;
    const selView = view.selected;
    if (selView?.dataSchema?.labels?.includes(NodeType.WorkspaceSchema)) return;
    const { searchData } = getAPI();
    if (!ignoreLoading) dispatch(setLoading(true));
    try {
      const pagination = {
        npp: numOfResults,
        hasMore: true
      };
      const { searchHash, payload } = getSearchPayload(
        view,
        selView,
        planning,
        null,
        numOfResults + 1
      );
      if (view.searchHash != searchHash || forceReload) {
        const { data, info } = await searchData(payload);
        if (data?.length <= numOfResults) pagination.hasMore = false;
        else if (data) data.pop();
        dispatch(
          updateViewState(selView, {
            data,
            pagination,
            searchHash,
            info
          })
        );
      }
    } finally {
      if (!ignoreLoading) dispatch(setLoading(false));
    }
  };

const setViewSettings =
  (
    settings: UserSettingProps,
    src?: View.Setting | Schema | string
  ): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    if (src) dispatch(setUserSetting(src, settings));
    else {
      const view = getState().view?.selected;
      if (view) {
        dispatch(setUserSetting(view, settings));
      }
    }
  };

const setPersonalizedView =
  (
    settings: UserSettingProps,
    src: View.Setting | Schema | string
  ): ElementAction<Functions, S> =>
  async dispatch => {
    await dispatch(setViewSettings(settings, src));
    dispatch(loadData({ forceReload: true }));
  };

const updateExpiryView =
  (expiryParams: ExpiryRequestCallbackParams): ElementAction<Functions, S> =>
  async (dispatch, getState) => {
    try {
      const viewState = getState().view;
      const isMyExpiryDashboard =
        viewState?.selected?.props?.isMyExpiryDashboard;
      Object.keys(viewState.cards).forEach(key => {
        const card = viewState?.cards[key];
        const itemId = card?.props?.itemId;
        const itemRefId = card?.props?.refId;
        if (itemId == expiryParams?.itemId) {
          if (!expiryParams?.expiryRequestType) {
            card.props.expiryRequestType = null;
            card.props.expiryRequestJustification = null;
            card.props.expiryRequestDate = null;
            card.props.expiryDate = expiryParams.expiryDate;
            if (isMyExpiryDashboard) {
              viewState.groups.forEach(g => {
                const itemOccurrences =
                  g.cardRefIds.filter(refId => refId == itemRefId)?.length || 0;
                g.cardRefIds = g.cardRefIds.filter(refId => refId != itemRefId);
                g.count = g.count - itemOccurrences;
              });
            }
          } else {
            card.props.expiryRequestType = expiryParams?.expiryRequestType;
            card.props.expiryRequestJustification =
              expiryParams?.expiryRequestJustification;
            card.props.expiryRequestByName = expiryParams?.expiryRequestByName;
            card.props.expiryRequestDate = formatDate(new Date(), true);
          }
        }
      });
      dispatch(updateViewState(viewState?.selected, viewState));
    } catch (e) {
      console.error(`Failed to update expiry view with error`, e);
    }
  };

const setBackingUpFor =
  (backingUpFor: any): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const viewState = getState().view;
    dispatch(updateViewState(viewState?.selected, { backingUpFor }));
    dispatch(reloadData());
  };

const setViewingAs =
  (viewingAs: any, actAs?: boolean): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const viewState = getState().view;
    const view = viewState?.selected;
    if (!viewingAs) {
      const { getUserSettings } = getAPI();
      const userSettings = await getUserSettings();
      dispatch(updatePlanningState({ userSettings }));
      dispatch(
        updateViewState(viewState?.selected, {
          viewingAs,
          actingAs: actAs ? viewingAs : null,
          filters: applyUserSettingFilters(view, userSettings),
          sortBy: applyUserSettingSortBys(view, userSettings)
        })
      );
    } else {
      dispatch(
        updateViewState(viewState?.selected, {
          viewingAs,
          actingAs: actAs ? viewingAs : null
        })
      );
    }
    dispatch(reloadData(null, true)); // Force reload is set to true
  };

const setEditMode =
  (editMode: any): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const viewState = getState().view;
    dispatch(updateViewState(viewState?.selected, { editMode }));
  };

const setUnsavedChangesBoolean =
  (hasUnsavedChanges: boolean): ElementAction<Functions, S> =>
  dispatch => {
    dispatch(setUnsavedChanges(hasUnsavedChanges));
  };

const exportRolePriorityTree =
  (toaster: any): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    toaster.show({ header: 'Export', messages: ['Downloading file'] });
    try {
      const { exportRolePriorityTree } = getAPI();
      const data = await exportRolePriorityTree();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `role-priority-journey-stepdata-export-${
        new Date().toISOString().split('T')[0]
      }.rpjs`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toaster.success({
        header: 'Export',
        messages: ['File downloaded successfully']
      });
    } catch (error) {
      toaster.error({ header: 'Export', messages: ['Failed to export data'] });
    }
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    toggleCheckbox: (refId?: string) => dispatch(toggleViewCheckbox(refId)),
    setBackingUpFor: (b: any) => dispatch(setBackingUpFor(b)),
    setViewingAs: (b: Metadata) => dispatch(setViewingAs(b)),
    setActingAs: (b: Metadata) => dispatch(setViewingAs(b, true)),
    setEditMode: (b: any) => dispatch(setEditMode(b)),
    enableEditMode: () => dispatch(enableEditMode()),
    disableEditMode: () => dispatch(disableEditMode()),
    setUnsavedChangesBoolean: (hasUnsavedChanges: boolean) =>
      dispatch(setUnsavedChangesBoolean(hasUnsavedChanges)),
    setViewSettings: (
      s: UserSettingProps,
      src?: View.Setting | Schema | string
    ) => dispatch(setViewSettings(s, src)),
    selectView: (v: View.Setting, w?: Workspace) => dispatch(selectView(v, w)),
    viewDataInSchema: (s: Schema) => dispatch(viewDataInSchema(s)),
    loadGroupData: async (g: View.Group, limit?: number) =>
      dispatch(loadGroupData(g, limit)),
    loadSwimlane: async (
      group: View.Group,
      swimlane: View.Group,
      limit?: number
    ) => dispatch(loadSwimlane(group, swimlane, limit)),
    toggleSwimlane: (swimlaneId: string, expanded: boolean) =>
      dispatch(toggleSwimlane(swimlaneId, expanded)),
    setTempFilters: (filters: View.FiltersField[]) =>
      dispatch(setTempFilters(filters)),
    setFilters: (filters: View.FiltersField[], remove?: boolean) =>
      dispatch(setFilters(filters, remove)),
    setFilter: (filter: View.FiltersField) => dispatch(setFilter(filter, null)),
    removeFilter: (filter: View.FiltersField) =>
      dispatch(setFilter(filter, true)),
    addFilter: (filter: View.FiltersField) =>
      dispatch(setFilter(filter, false, true)),
    addAnotherFilter: (filter: View.FiltersField) =>
      dispatch(setFilter(filter, false, true, true)),
    setSortBy: (sortBy: View.SortByField[]) => dispatch(setSortBy(sortBy)),
    reloadData: (keepExpanded?: boolean) => dispatch(reloadData(keepExpanded)),
    getBoardCardCount: () => dispatch(getBoardCardCount()),
    increaseNumOfResultsAndLoad: (cb?: Function) =>
      dispatch(increaseNumOfResultsAndLoad(cb)),
    updateExpiryView: (expiryParams: any) =>
      dispatch(updateExpiryView(expiryParams)),
    updatesInvalidCells: (refId: string, field: string, serverSide?: boolean) =>
      dispatch(updatesInvalidCells(refId, field, serverSide)),
    addInvalidCell: (refId: string, field: string, serverSide?: boolean) =>
      dispatch(addInvalidCell(refId, field, serverSide)),
    removeInvalidCell: (refId: string, field: string) =>
      dispatch(removeInvalidCell(refId, field)),
    resetInvalidCells: () => dispatch(dispatch(updateInvalidCells([]))),
    setPersonalizedView: (
      s: UserSettingProps,
      src: View.Setting | Schema | string
    ) => dispatch(setPersonalizedView(s, src)),
    exportRolePriorityTree: (toaster: any) =>
      dispatch(exportRolePriorityTree(toaster))
  };
};

export const getSearchPayload = (
  view: ViewState,
  selView: View.Setting,
  planning: PlanningState,
  group?: View.Group,
  limit?: number,
  swimlane?: View.Group
) => {
  if (!selView) return null;
  selView = clone(selView);
  const userSetting = planning?.userSettings[selView?.props?.refId];
  const filters = cleanFilter(
    getSearchFilters(selView.props?.preApplyFilters, view.filters)
  );

  if (!!group?.filters) {
    filters.push(...group.filters);
  }
  if (!!swimlane?.filters) {
    filters.push(...swimlane.filters);
    if (view.groups) {
      const refIds = view.groups
        .map(g => (g.props ? g.props.refId : null))
        .filter(x => x);
      const field = selView.props.groupBy[0].field;
      if (refIds.length) {
        filters.push({
          field,
          conditions: [{ [Op.refIds]: refIds }]
        });
      }
    }
  }
  const sortBy = view.sortBy;
  if (selView?.props?.groupBy?.length > 1) {
    const viewSetting = planning?.userSettings[selView.props.refId];
    if (viewSetting?.props?.hideSwimlanes) {
      selView.props.groupBy = selView.props.groupBy.slice(0, 1);
    }
  }

  if (!!userSetting) {
    selView.card.props.fields = getCardFields(selView.card, userSetting);
  }
  const missingRequiredFilters = getMissingRequiredFilters(
    view,
    selView,
    planning
  );
  const payload: View.SearchPayload = {
    isRegressionMode: planning.regressionMode,
    workspaceRefId: planning.selectedWorkspace.props?.refId,
    view: includeGcomStatus(selView),
    missingRequiredFilters,
    includeCards: undefined,
    filters,
    sortBy,
    limit
  };
  if (showBackupMode(selView) && !!view.backingUpFor) {
    payload.backingUpFor = view.backingUpFor;
  }
  if (view?.selected?.isMyItemsView && !!view.viewingAs?.props) {
    const { empCd, name } = view.viewingAs?.props;
    payload.viewingAs = { empCd, name };
  }
  if (!!swimlane) payload.includeCards = true;
  const searchHash = generateHashCode({
    filters,
    sortBy,
    missingRequiredFilters: missingRequiredFilters?.map(f => f.key)
  });
  return { searchHash, payload };
};

const getMissingRequiredFilters = (
  view: ViewState,
  selView: View.Setting,
  planning: PlanningState
): SchemaField[] => {
  const missingRequiredFilters = [];
  const appliedFilters = excludeEmptyFilters(view.filters);
  selView.props.filters?.forEach(f => {
    if (f.isRequired && !findOne(appliedFilters, { field: f.field })) {
      const field = parseField(f.field, planning.selectedWorkspace.schemas);
      if (field.schemaField != null) {
        missingRequiredFilters.push(field.schemaField);
      }
    }
  });
  if (missingRequiredFilters.length > 0) {
    return missingRequiredFilters;
  }
};

const includeGcomStatus = (view: View.Setting) => {
  if (
    view?.card?.props?.fields &&
    view.props.data == NodeType.Item &&
    view.props.type == ViewType.Kanban
  ) {
    if (
      view.card.props.fields.filter(f => {
        const s = f.field ? f.field : f;
        if (s == 'Item.gcomStatus') return true;
        return false;
      })[0] == null
    ) {
      view.card.props.fields.push({ field: 'Item.gcomStatus' });
    }
  }
  return view;
};

export const updatesInvalidCells =
  (refId: string, field: string, serverSide?: boolean) =>
  (dispatch: Dispatch) => {
    const newList = [{ refId, field, serverSide }];

    dispatch(updateInvalidCells(newList));
  };

export const addInvalidCell =
  (refId: string, field: string, serverSide?: boolean) =>
  (dispatch: Dispatch, getState: () => S) => {
    const invalidCells = getState().view.invalidCells;
    const updatedInvalidCells = invalidCells.some(
      cell => cell.refId === refId && cell.field === field
    )
      ? invalidCells
      : [...invalidCells, { refId, field, serverSide }];

    dispatch(updateInvalidCells(updatedInvalidCells));
  };

export const removeInvalidCell =
  (refId: string, field: string) => (dispatch: Dispatch, getState: () => S) => {
    const invalidCells = getState().view.invalidCells;
    const updatedInvalidCells = invalidCells.filter(
      cell => cell.refId !== refId || cell.field !== field
    );

    dispatch(updateInvalidCells(updatedInvalidCells));
  };
